<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row dense>
      <v-col
        v-if="label"
        class="labeled-input"
        cols="12"
      >
        <label :for="inputId">
          {{ label }}
          <RequiredIndicator v-if="mandatory" />
        </label>
      </v-col>

      <v-col
        v-else-if="title || subtitle"
        cols="12"
      >
        <div
          :class="[titleClasses, bold ? 'fs-16 fw-600' : 'fs-16 fw-500']"
          class="c-black"
        >
          {{ $bct(title) }}
          <RequiredIndicator v-if="mandatory" />
        </div>

        <div
          v-if="subtitle"
          class="fs-14 c-light-black mt-1 text-pre-wrap"
        >
          {{ $bct(subtitle) }}
        </div>
      </v-col>

      <v-col>
        <v-select
          @blur="handleBlur"
          @update:model-value="handleUpdateModelValue"
          :id="inputId"
          :append-inner-icon="appendIcon"
          :aria-label="ariaLabel"
          :chips="chips"
          :clearable="clearable"
          :closable-chips="deletableChips"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-props="itemProps"
          :item-title="itemTitle"
          :item-value="itemValue"
          :items="items"
          :menu-props="{ 'data-testid': menuTestId }"
          :model-value="localValue"
          data-cy="labeled-select"
          data-testid="labeled-select"
          variant="filled"
          hide-details
          tile
          v-bind="filteredAttrs"
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import _ from 'lodash';
import { onUpdated } from 'vue';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';
import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import propsToRefs from '@/shared/utils/propsToRefs';

const attrs = useAttrs();
const $bct = inject('translator');

defineEmits(useLabeledField.emits);

const props = defineProps({
  chips: Boolean,
  clearable: {
    type: Boolean,
    default: true,
  },
  deletableChips: Boolean,
  id: {
    type: String,
    default: undefined,
  },
  items: {
    type: Array,
    default: () => [],
  },
  itemProps: Boolean,
  menuTestId: {
    type: String,
    default: undefined,
  },
  itemTitle: {
    type: String,
    default: 'text',
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  subtitle: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  titleClasses: {
    type: [String, Array, Object],
    default: undefined,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const localValue = defineModel({ type: [String, Number, Array] });

const inputId = ref(props.id || _.uniqueId('labeled-select-'));

const filteredAttrs = ref(attrsOmitDataPrefix(attrs));

const { appendIcon, handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, ariaLabel, label, locked } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

// attrs is not a reactive object, so we update the filteredAttrs ref on every DOM tree update
onUpdated(() => {
  filteredAttrs.value = attrsOmitDataPrefix(attrs);
});
</script>
